/* eslint-disable no-constant-condition */
import React, { useState, useEffect } from "react";
// ANT DESIGN COMPONENTS
import { Select } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// CUSTOM HOOKS
import useGetAllMessages from "../../../customHooks/GET_requests/useGetAllMessages";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { MessageInterface } from "../../../interfaces/Message.interface";

export default function SelectMessagesMultiple({
  inputValue,
  inputOnChange,
  disabledInput,
  displaySelect,
  messageType = "",
  width = "300px",
  allowedIds = null,
}: {
  inputValue: string[];
  inputOnChange: (ids: string[]) => void;
  disabledInput: boolean;
  displaySelect: boolean;
  messageType?: string;
  /** Select width; defaults to the historical fixed 300px used by the
   * dedicated Bank Actions screen. Pass "100%" to fill a full-width row
   * (e.g. the setup wizard's Valid States rows). */
  width?: string | number;
  /**
   * Restrict the OFFERED options to these catalog ids — the messages one
   * bank's adapter actually speaks.
   *
   * `null` (the default) offers the whole catalog, which is what every existing
   * caller does and what Valid States has always done. An EMPTY array is not a
   * restriction either: a bank whose adapter declares no message keys would
   * otherwise be given a picker with nothing in it, and a control an operator
   * cannot use is worse than an unfiltered one. Say which of the two is in
   * force next to the field rather than leaving them to guess.
   *
   * Ids already SELECTED are always offered, whatever the list says. Otherwise
   * re-scoping a bank, or loading a row saved before the adapter changed, would
   * render the stored value as a raw id the operator cannot read or keep.
   */
  allowedIds?: string[] | null;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option } = Select;
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const [data, setData] = useState<MessageInterface[]>([]);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { messages, isLoadingGetAllMessages, errorMessageGetAllMessages, isErrorGetAllMessages } =
    useGetAllMessages();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChange = (newValues: string[]) => {
    inputOnChange(newValues || []);
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (messages) {
      let filteredMessages = messages;

      if (messageType) {
        const normalizedType = messageType.toUpperCase();
        filteredMessages = messages.filter(
          (msg: MessageInterface) => msg.message_type?.name?.toUpperCase() === normalizedType,
        );
      }

      setData([...filteredMessages]);
    }
  }, [messages, messageType]);

  // Options actually offered. `data` stays unfiltered so the read-only branch
  // below can still NAME a selected message that the allow-list excludes —
  // hiding it there would show the operator "--" for a value that is set.
  const options =
    allowedIds && allowedIds.length > 0
      ? data.filter((item) => allowedIds.includes(item.id) || inputValue?.includes(item.id))
      : data;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className=" generalStyles__width100">
      {displaySelect ? (
        <Select
          mode="multiple"
          loading={isLoadingGetAllMessages}
          size="middle"
          className="generalStyles__width100"
          value={inputValue}
          placeholder={t("general.message")}
          onChange={onChange}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            typeof option?.label === "string" &&
            option.label.toLowerCase().includes(input.toLowerCase())
          }
          allowClear
          disabled={disabledInput}
          maxTagCount="responsive"
          style={{ width }}
        >
          {options.map((item: MessageInterface) => (
            <Option key={item.id} value={item.id} label={item.shortMessage}>
              <span className="generalStyles__info">
                {item && item.message_type && item.message_type.name ? item.message_type.name : ""}
              </span>
              : {item.shortMessage}
            </Option>
          ))}
        </Select>
      ) : (
        (() => {
          const selectedMessages = data.filter(
            (p: MessageInterface) => inputValue && inputValue.includes(p.id),
          );
          return (
            <span className="generalStyles__width100">
              {selectedMessages.length > 0
                ? selectedMessages.map((msg, index) => (
                    <span key={msg.id}>
                      <span className="generalStyles__info">{msg?.message_type?.name || ""}</span>:{" "}
                      {msg.shortMessage || "--"}
                      {index < selectedMessages.length - 1 ? ", " : ""}
                    </span>
                  ))
                : "--"}
            </span>
          );
        })()
      )}
      <GlobalAlert2
        isError={isErrorGetAllMessages}
        isSuccess={false}
        requestType="GET"
        error={errorMessageGetAllMessages}
        name="Messages"
      />
    </div>
  );
}
